<template>
    <transition-group
        tag="div"
        :class="$style['sub-items']"
        :enter-class="$style['sub-item-transition-enter']"
        :leave-to-class="$style['sub-item-transition-leave-to']"
        :leave-active-class="$style['sub-item-transition-leave-active']"
    >
        <template v-for="{ subCollection, i, depth } in items">
            <div
                v-show="
                    subCollection.Settings == null ||
                    subCollection.Settings.invisibleOnFilters == false
                "
                :class="['sub-item-depth-' + (depth - (parentDepth - 1))]"
                v-if="
                    shouldShowItem(
                        subCollection,
                        collection,
                        i,
                        collections,
                        value
                    )
                "
                :key="subCollection.Id"
                :data-key="subCollection.Id"
            >
                <!-- TODO: common/checkbox-item.vue -->
                <CheckboxItem
                    :id="'collectionCheckBox_' + subCollection.Id"
                    :href="
                        subCollection.Filler === true
                            ? '#'
                            : getCollectionUrl(
                                  subCollection,
                                  viewMode,
                                  baseUrl,
                                  value,
                                  root ?? [],
                                  matchedLogicalName,
                                  matchedLogicalNameEntityGuid,
                                  matchedLogicalNameModuleGuid,
                                  matchedLogicalNameSubModuleGuid
                              ) ?? '#'
                    "
                    :checked="
                        isChecked(
                            value,
                            collections.indexOf(collection),
                            subCollection.Id
                        )
                    "
                    :name="getName(subCollection)"
                    @select="
                        setChecked(
                            collections.indexOf(collection),
                            subCollection.Id,
                            $event,
                            depth - (parentDepth - 1)
                        )
                    "
                ></CheckboxItem>
                <!--<span :class="$style['product-count']" v-if="subCollection.hasOwnProperty('ProductCount')">({{subCollection.ProductCount}})</span>-->
            </div>
        </template>
        <template
            v-if="
                items.filter((x) => x.subCollection.ProductCount !== 0).length >
                limitSubItems
            "
        >
            <div
                :class="$style['show-more']"
                key="show-all"
                v-if="!isUnlimited(collection)"
                v-show="
                    collection.Settings == null ||
                    collection.Settings.invisibleOnFilters == false
                "
                @click="applyShowAll(collection)"
            >
                {{
                    labels.get(
                        "frontendProductList.filters.productCollection.showMore"
                    )
                }}
            </div>
            <div
                :class="$style['show-more']"
                key="hide-all"
                v-if="
                    collection.Settings?.alwaysExpanded != true &&
                    isUnlimited(collection)
                "
                @click="stopShowAll(collection)"
            >
                {{
                    labels.get(
                        "frontendProductList.filters.productCollection.showMore_close"
                    )
                }}
            </div>
        </template>
    </transition-group>
</template>

<style module>
:global(.sub-item-depth-0) {
    margin-left: 0;
}
:global(.sub-item-depth-1) {
    margin-left: 10px;
}
:global(.sub-item-depth-2) {
    margin-left: 20px;
}

.checkbox {
    vertical-align: text-top;
}

.sub-item-transition-enter,
.sub-item-transition-leave-to {
    opacity: 0;
    height: 0;
}

.sub-item-transition-leave-active {
    /*position: absolute;*/
}

.sub-items {
    display: block;
}

.sub-item,
.show-more {
    transition: all 0.4s;
}

.show-more {
    color: #272727;
    font-size: 14px;
    font-style: italic;
    text-decoration: underline;
    cursor: pointer;
    height: 20px;
}
</style>

<script setup lang="ts">
import { type PropType, ref } from "vue";
import {
    type MappedProductCollectionModel,
    type ProductCollectionModel,
} from "../../models";
import { type IsCheckedMethod, type SetCheckedMethod } from "./types";
import { getCollectionUrl } from "../../collection-url";
import labels from "../../../../shared/labels";
import CheckboxItem from "../common/checkbox-item.vue";

const props = defineProps({
    collection: {
        type: Object as PropType<MappedProductCollectionModel>,
        required: true,
    },
    debugProductCounts: Boolean,
    collections: {
        type: Array as PropType<MappedProductCollectionModel[]>,
        required: true,
    },
    items: {
        type: Array as PropType<
            {
                subCollection: MappedProductCollectionModel;
                i: number;
                depth: number;
            }[]
        >,
        required: true,
    },
    parentDepth: {
        type: Number,
        required: true,
    },
    setChecked: {
        type: Function as PropType<SetCheckedMethod>,
        required: true,
    },

    isChecked: {
        type: Function as PropType<IsCheckedMethod>,
        required: true,
    },
    viewMode: {
        type: Number,
        required: true,
    },
    baseUrl: String,
    value: Array as PropType<number[][]>,
    root: {
        type: Array as PropType<MappedProductCollectionModel[]>,
        required: true,
    },
    matchedLogicalName: String,
    matchedLogicalNameEntityGuid: Number,
    matchedLogicalNameModuleGuid: Number,
    matchedLogicalNameSubModuleGuid: Number,
});

function getName(col: MappedProductCollectionModel) {
    if (col.Filler) return undefined;
    let name = col.Name;

    if (props.debugProductCounts) {
        name += ` ${col.ProductCount}`;
    }
    return name;
}

const limitSubItems = ref(8);
const unlimitSubItemsForCollectionId = ref<number[]>([]);

function shouldShowItem(
    subCollection: ProductCollectionModel,
    collection: ProductCollectionModel,
    i: number,
    collections: ProductCollectionModel[],
    value: number[][] | null | undefined
) {
    const limit = getLimitForCollection(collection);
    const isBetweenLimit = limit <= 0 || i < limit;
    const checked = props.isChecked(
        value,
        collections.indexOf(collection),
        subCollection.Id
    );

    if (subCollection.ProductCount === 0 && !checked) return false;

    return isBetweenLimit || checked;
}

function getLimitForCollection(collection: ProductCollectionModel) {
    if (isUnlimited(collection)) {
        return 0;
    }
    return limitSubItems.value;
}

function applyShowAll(collection: ProductCollectionModel) {
    if (!isUnlimited(collection)) {
        unlimitSubItemsForCollectionId.value.push(collection.Id);
    }
}
function stopShowAll(collection: ProductCollectionModel) {
    while (isUnlimited(collection)) {
        const index = unlimitSubItemsForCollectionId.value.indexOf(
            collection.Id
        );
        unlimitSubItemsForCollectionId.value.splice(index, 1);
    }
}
function isUnlimited(collection: ProductCollectionModel) {
    if (collection.Settings?.alwaysExpanded == true) return true;
    return unlimitSubItemsForCollectionId.value.includes(collection.Id);
}
</script>
